import {FC, useMemo} from 'react'
import FullCalendar from '@fullcalendar/react'
import type {EventClickArg, EventContentArg} from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import {
  AdminTask,
  eventIdToTaskId,
  STATUS_COLORS,
  statusColor,
  TASK_STATUSES,
  tasksToEvents,
} from '../adminDashboard/taskFilters'

type Props = {
  // Already-FILTERED tasks (same array the list view renders), so both views stay in sync.
  tasks: AdminTask[]
  // Click a task event to open it (wired to the dashboard's edit modal).
  onTaskClick?: (taskId: number) => void
}

// Human label per status for the legend (falls back to the raw code).
const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  cancelled: 'Cancelled',
}

// Renders a task inside the calendar cell: time + title, with the linked member/partner/activity as
// a subtle second line so the calendar reflects the task's links, not just its title.
const renderEventContent = (arg: EventContentArg) => {
  const p = arg.event.extendedProps as {
    members?: string[]
    partners?: string[]
    activities?: string[]
  }
  const links = [...(p.members ?? []), ...(p.partners ?? []), ...(p.activities ?? [])]
  return (
    <div className='nb-calendar-event'>
      <div className='nb-calendar-event__title'>
        {arg.timeText && <span className='nb-calendar-event__time'>{arg.timeText} </span>}
        {arg.event.title}
      </div>
      {links.length > 0 && <div className='nb-calendar-event__links'>{links.join(' · ')}</div>}
    </div>
  )
}

const CalendarWrapper: FC<Props> = ({tasks = [], onTaskClick}) => {
  const events = useMemo(() => tasksToEvents(tasks), [tasks])
  // Tasks with no due date can't be placed on the calendar — surface how many are hidden.
  const undatedCount = tasks.length - events.length

  const handleEventClick = (arg: EventClickArg) => {
    const id = eventIdToTaskId(arg.event.id)
    if (id != null) onTaskClick?.(id)
  }

  return (
    <div className='nb-admin-calendar'>
      <div className='nb-calendar-legend'>
        {TASK_STATUSES.map((status) => (
          <div key={status} className='nb-calendar-legend__item'>
            <span
              className='nb-calendar-legend__swatch'
              style={{backgroundColor: STATUS_COLORS[status] ?? statusColor(status)}}
            />
            <span>{STATUS_LABELS[status] ?? status}</span>
          </div>
        ))}
        {undatedCount > 0 && (
          <div className='nb-calendar-legend__note'>
            {undatedCount} task{undatedCount === 1 ? '' : 's'} without a due date not shown
          </div>
        )}
      </div>

      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
        locale='en'
        initialView='dayGridMonth'
        height='auto'
        nowIndicator
        editable={false}
        selectable={false}
        dayMaxEvents={3}
        titleFormat={{year: 'numeric', month: 'long'}}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
        }}
        buttonText={{today: 'Today', month: 'Month', week: 'Week', day: 'Day', list: 'List'}}
        events={events}
        eventContent={renderEventContent}
        eventClick={handleEventClick}
      />
    </div>
  )
}

export {CalendarWrapper}
